import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import LinkIcon from '@atlaskit/icon/glyph/link';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { messages as databasesMessages } from '@databases/intl/messages';
import { ViewId } from '@databases/types/database/view';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import useCopyViewLink from '../../hooks/useCopyViewLink';
import useView from '../../hooks/useView';
import { useViewUiState } from '../../hooks/useViewUiState';
import { messages } from '../../i18n';
import { ViewPanelSettingType } from '../../types/types';

const headerContainerStyles = xcss({
  padding: 'space.300',
  paddingBottom: 'space.150',
  width: '100%',
  position: 'sticky',
  top: 'space.0',
  zIndex: 'navigation',
  backgroundColor: 'elevation.surface',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'color.border',
});

export interface ViewSettingsHeaderProps {
  contentId: string;
  onDelete: () => void;
  onClose: () => void;
  hasChanges: boolean | undefined;
  onBack: () => void;
  isOverview: boolean;
  canEditViews: boolean;
  totalViews: number;
}

const ViewSettingsHeader: FunctionComponent<ViewSettingsHeaderProps> = ({
  contentId,
  onDelete,
  onClose,
  hasChanges,
  onBack,
  isOverview,
  canEditViews,
  totalViews,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const { formatMessage } = useIntl();
  const client = useAnalytics();
  const { view } = useView();
  const { activePanelSetting } = useViewUiState();
  const onCopyLink = useCopyViewLink(contentId);

  const handleCopyLink = useCallback(() => {
    const result = onCopyLink(view);

    if (result) {
      setIsCopied(true);

      client.sendViewLinkCopiedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'databaseViewSettingsPanel',
        viewId: view.id,
      });

      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    }
  }, [client, contentId, onCopyLink, view]);

  const handleClose = useCallback(() => {
    client.sendViewSettingsPanelClosedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'databaseViewSettingsPanel',
      viewId: view.id,
      isEdited: hasChanges,
    });
    onClose();
  }, [client, contentId, hasChanges, onClose, view]);

  const heading = useMemo(() => {
    switch (activePanelSetting) {
      case ViewPanelSettingType.FILTER:
        return formatMessage(messages['view-settings.filter.filter-text']);
      case ViewPanelSettingType.LAYOUT:
        return formatMessage(messages['view-settings.layout.layout-heading']);
      case ViewPanelSettingType.SORT:
        return formatMessage(messages['view-settings.sort.sort-text']);

      case ViewPanelSettingType.FIELDS:
        return formatMessage(messages['view-settings.hide-field.field-text']);
      default:
        return formatMessage(messages['view-settings-panel.title']);
    }
  }, [activePanelSetting, formatMessage]);

  return (
    <Inline spread="space-between" alignBlock="center" xcss={headerContainerStyles}>
      <Inline alignInline="start" alignBlock="center" space="space.050">
        {!isOverview && (
          <Tooltip content={formatMessage(messages['view-settings-panel.back'])}>
            <Button
              appearance="subtle"
              iconBefore={
                <ArrowLeftIcon
                  label={formatMessage(messages['view-settings-panel.back'])}
                />
              }
              onClick={onBack}
              testId={'view-settings-panel-back-button'}
            />
          </Tooltip>
        )}
        <Heading as="h2" size="small">
          {heading}
        </Heading>
      </Inline>

      <Inline space="space.050">
        <Tooltip
          content={formatMessage(
            isCopied
              ? // TODO: design to explore a more accessible feedback mechanism as feedback tooltips are not recommended for a11y
                databasesMessages['copy-link.tooltip']
              : messages['view-settings.copy-link']
          )}
          testId="view-settings-panel-copy-link-tooltip"
        >
          {isOverview && (
            <Button
              appearance="subtle"
              iconBefore={
                <LinkIcon label={formatMessage(messages['view-settings.copy-link'])} />
              }
              onClick={handleCopyLink}
              testId={'view-settings-panel-copy-link-button'}
            />
          )}
        </Tooltip>
        {isOverview && canEditViews && view.id !== ViewId.UNSAVED && totalViews > 1 && (
          <Tooltip
            content={formatMessage(messages['view-settings-panel.delete'])}
            testId="view-settings-panel-delete-tooltip"
          >
            <Button
              appearance="subtle"
              iconBefore={
                <TrashIcon
                  label={formatMessage(messages['view-settings-panel.delete'])}
                />
              }
              onClick={onDelete}
              testId={'view-settings-panel-delete-button'}
            />
          </Tooltip>
        )}
        <Tooltip
          content={
            hasChanges &&
            formatMessage(messages['view-settings-panel.close.tooltip-text'])
          }
          testId={'view-settings-panel-close-button-tooltip'}
        >
          <Button
            appearance="subtle"
            iconBefore={
              <CrossIcon label={formatMessage(messages['view-settings-panel.close'])} />
            }
            onClick={handleClose}
            testId={'view-settings-panel-close-button'}
          />
        </Tooltip>
      </Inline>
    </Inline>
  );
};

export default ViewSettingsHeader;
